<script setup lang="ts">
import { computed } from 'vue'
import type { ExchangeItem } from '@/types/shared/exchange'
import { EXCHANGE_ITEM_COST_TYPE } from '@/types/shared/exchange'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { usePlayer } from '@/composables/game/player'
import { rewardImageMap, getExchangeRewardDisplay } from '@/composables/game/exchange-rewards'

const props = defineProps<{
  itemId: string
  item: ExchangeItem
  hideCost?: boolean
}>()

const emit = defineEmits<{
  (e: 'purchase'): void
  (e: 'pay'): void
  (e: 'outOfSpecialCurrency', currencyId: string): void
}>()

const { player, goldenCoinValue } = usePlayer()

const rewardDisplay = computed(() => {
  return getExchangeRewardDisplay(props.item.reward, goldenCoinValue.value)
})

const stepCostLabel = computed(() => {
  if (props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.FREE) {
    return 'FREE'
  }
  if (props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.STAR) {
    return `$${props.item.cost.usdAmount?.toFixed(2)}`
  }
  return formatNumberFullTruncate(props.item.cost.amount)
})

const costImage = computed(() => {
  if (!props.item.cost || props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.FREE || props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.STAR) {
    return undefined
  }

  if (props.item.cost.type === EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY && props.item.cost.currencyId) {
    return rewardImageMap.value[props.item.cost.currencyId]
  }
  return undefined
})

function handleClick() {
  if (props.item.cost?.type === EXCHANGE_ITEM_COST_TYPE.STAR) {
    emit('purchase')
    return
  }

  if (props.item.cost?.type === EXCHANGE_ITEM_COST_TYPE.SPECIAL_CURRENCY) {
    const balance = player.value.specialCurrencies[props.item.cost.currencyId] || 0
    const required = props.item.cost.amount || 0
    if (balance < required) {
      emit('outOfSpecialCurrency', props.item.cost.currencyId)
      return
    }
  }

  emit('pay')
}
</script>

<template>
  <div
    class="relative mb-1 flex w-full min-w-[100px] cursor-pointer flex-col items-center transition-transform hover:scale-105"
    @click="handleClick"
  >
    <div class="relative mb-1 flex flex-col items-center justify-center">
      <img
        src="@/assets/images/exchange/exchange-podium.webp"
        class="absolute -bottom-5 min-w-[92px]"
        alt="Reward Podium"
      >

      <!-- Main reward icon -->
      <div class="-mb-3 flex size-[42px] items-center justify-center">
        <img
          v-if="rewardDisplay"
          :src="rewardDisplay.image"
          :alt="rewardDisplay.title"
          class="relative h-full w-full object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.7)]"
        >
      </div>

      <!-- Reward amount overlay -->
      <div
        v-if="rewardDisplay"
        class="flex items-center justify-center z-10 -mt-1"
      >
        <span class="reward-amount-text text-[#FFF8E6] text-center font-pilat text-2xl font-black leading-8 uppercase">
          {{ rewardDisplay.amount }}
        </span>
      </div>
    </div>

    <!-- Bottom cost button -->
    <Transition
      name="fade"
      mode="out-in"
    >
      <button
        v-if="!hideCost"
        class="exchange-claim-btn min-w-[94px] max-w-[94px] h-7 rounded-[6px] flex items-center justify-center text-base leading-5 font-bold text-white border-none cursor-pointer backdrop-blur-[12px] transition-all duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed active:bg-[rgba(242,156,17,0.66)]"
      >
        <img
          v-if="costImage"
          class="mr-1 h-4 w-auto object-cover drop-shadow-[0_0_3px_rgba(0,0,0,0.88)]"
          :src="costImage"
          :alt="stepCostLabel"
        >
        {{ stepCostLabel }}
      </button>
    </Transition>
  </div>
</template>

<style scoped>
.exchange-claim-btn {
  background: rgba(242, 156, 17, 0.44);
  box-shadow: 0 0 12px 0 rgba(255, 221, 164, 0.66);
}

.reward-amount-text {
  text-shadow: 0 1.319px 2.638px rgba(0, 0, 0, 0.32);
  -webkit-text-stroke-width: 2px;
  -webkit-text-stroke-color: #0D0C0E;
  paint-order: stroke fill;
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.9));
}
</style>
